import React, {
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    useDispatch,
    useSelector,
} from "react-redux";

import {
    useHistory,
} from "react-router-dom";

import {
    FiArrowLeft,
    FiBriefcase,
    FiCheckCircle,
    FiEye,
    FiFilter,
    FiRefreshCw,
    FiSearch,
    FiShield,
    FiSlash,
    FiUser,
    FiUsers,
    FiX,
    FiXCircle,
} from "react-icons/fi";

import useSwal
    from "../../../../../hooks/useSwal";


import {
    fetchPlatformTenantUsers,
    fetchPlatformTenantUserFilters,
    updatePlatformTenantUserStatus,

    setPlatformTenantUserFilter,

    selectPlatformTenantUsers,
    selectPlatformTenantUserPagination,
    selectPlatformTenantUserFilters,
    selectPlatformTenantUserFilterOptions,
    selectPlatformTenantUserListLoading,
    selectPlatformTenantUserFiltersLoading,
    selectPlatformTenantUserStatusLoading,
    selectPlatformTenantUserStatusUserId,
} from "../../../../../store/platformTenantUser/platformTenantUserSlice";

import {
    BusinessStatusBadge,
} from "../business/components";

import "../business/PlatformBusiness.css";

import "./PlatformTenantUsers.css";


/*
|--------------------------------------------------------------------------
| FORMAT NUMBER
|--------------------------------------------------------------------------
*/

const formatNumber = (
    value
) => {

    return Number(
        value ||
        0
    ).toLocaleString(
        "en-IN"
    );

};


/*
|--------------------------------------------------------------------------
| FORMAT DATE
|--------------------------------------------------------------------------
*/

const formatDate = (
    value
) => {

    if (
        !value
    ) {

        return "-";

    }


    const date =
        new Date(
            value
        );


    if (
        Number.isNaN(
            date.getTime()
        )
    ) {

        return "-";

    }


    return date.toLocaleDateString(
        "en-IN",
        {
            day:
                "2-digit",

            month:
                "short",

            year:
                "numeric",
        }
    );

};


/*
|--------------------------------------------------------------------------
| GET USER ID
|--------------------------------------------------------------------------
*/

const getUserId = (
    user
) => {

    return (
        user?._id ||
        user?.id ||
        null
    );

};


/*
|--------------------------------------------------------------------------
| GET TENANT
|--------------------------------------------------------------------------
*/

const getTenant = (
    user
) => {

    if (
        user?.tenantId &&
        typeof user.tenantId ===
        "object"
    ) {

        return user.tenantId;

    }


    return null;

};


/*
|--------------------------------------------------------------------------
| GET ROLE
|--------------------------------------------------------------------------
*/

const getRole = (
    user
) => {

    if (
        user?.roleId &&
        typeof user.roleId ===
        "object"
    ) {

        return user.roleId;

    }


    return null;

};


/*
|--------------------------------------------------------------------------
| PLATFORM TENANT USER LIST
|--------------------------------------------------------------------------
*/

const PlatformTenantUserList =
    () => {

        const dispatch =
            useDispatch();


        const history =
            useHistory();


        const swal =
            useSwal();


        /*
        |--------------------------------------------------------------------------
        | REDUX
        |--------------------------------------------------------------------------
        */

        const users =
            useSelector(
                selectPlatformTenantUsers
            );


        const pagination =
            useSelector(
                selectPlatformTenantUserPagination
            );


        const filters =
            useSelector(
                selectPlatformTenantUserFilters
            );


        const filterOptions =
            useSelector(
                selectPlatformTenantUserFilterOptions
            );


        const listLoading =
            useSelector(
                selectPlatformTenantUserListLoading
            );


        const filtersLoading =
            useSelector(
                selectPlatformTenantUserFiltersLoading
            );


        const statusLoading =
            useSelector(
                selectPlatformTenantUserStatusLoading
            );


        const statusUserId =
            useSelector(
                selectPlatformTenantUserStatusUserId
            );


        /*
        |--------------------------------------------------------------------------
        | SEARCH
        |--------------------------------------------------------------------------
        */

        const [
            searchInput,
            setSearchInput,
        ] =
            useState(
                filters?.search ||
                ""
            );


        /*
        |--------------------------------------------------------------------------
        | LOAD FILTERS
        |--------------------------------------------------------------------------
        */

        useEffect(
            () => {

                dispatch(
                    fetchPlatformTenantUserFilters()
                );

            },
            [
                dispatch,
            ]
        );


        /*
        |--------------------------------------------------------------------------
        | LOAD USERS
        |--------------------------------------------------------------------------
        */

        useEffect(
            () => {

                dispatch(
                    fetchPlatformTenantUsers({
                        page:
                            filters?.page ||
                            1,

                        limit:
                            filters?.limit ||
                            20,

                        search:
                            filters?.search ||
                            "",

                        status:
                            filters?.status ||
                            "",

                        userType:
                            filters?.userType ||
                            "",

                        tenantId:
                            filters?.tenantId ||
                            "",

                        roleId:
                            filters?.roleId ||
                            "",
                    })
                );

            },
            [
                dispatch,
                filters?.page,
                filters?.limit,
                filters?.search,
                filters?.status,
                filters?.userType,
                filters?.tenantId,
                filters?.roleId,
            ]
        );


        /*
        |--------------------------------------------------------------------------
        | SYNC SEARCH
        |--------------------------------------------------------------------------
        */

        useEffect(
            () => {

                setSearchInput(
                    filters?.search ||
                    ""
                );

            },
            [
                filters?.search,
            ]
        );


        /*
        |--------------------------------------------------------------------------
        | ROLES FOR SELECTED TENANT
        |--------------------------------------------------------------------------
        */

        const visibleRoles =
            useMemo(
                () => {

                    const roles =
                        Array.isArray(
                            filterOptions?.roles
                        )
                            ? filterOptions.roles
                            : [];


                    if (
                        !filters?.tenantId
                    ) {

                        return roles;

                    }


                    return roles.filter(
                        (
                            role
                        ) => {

                            const roleTenantId =
                                String(
                                    role?.tenantId?._id ||
                                    role?.tenantId ||
                                    ""
                                );


                            return (
                                roleTenantId ===
                                String(
                                    filters.tenantId
                                )
                            );

                        }
                    );

                },
                [
                    filterOptions?.roles,
                    filters?.tenantId,
                ]
            );


        /*
        |--------------------------------------------------------------------------
        | PAGE SUMMARY
        |--------------------------------------------------------------------------
        */

        const summary =
            useMemo(
                () => {

                    return users.reduce(
                        (
                            result,
                            user
                        ) => {

                            const status =
                                String(
                                    user?.status ||
                                    ""
                                )
                                    .trim()
                                    .toUpperCase();


                            const userType =
                                String(
                                    user?.userType ||
                                    ""
                                )
                                    .trim()
                                    .toUpperCase();


                            result.total +=
                                1;


                            if (
                                status ===
                                "ACTIVE"
                            ) {

                                result.active +=
                                    1;

                            }


                            if (
                                status ===
                                "BLOCKED"
                            ) {

                                result.blocked +=
                                    1;

                            }


                            if (
                                status ===
                                "INACTIVE"
                            ) {

                                result.inactive +=
                                    1;

                            }


                            if (
                                userType ===
                                "OWNER"
                            ) {

                                result.owners +=
                                    1;

                            }


                            return result;

                        },
                        {
                            total:
                                0,

                            active:
                                0,

                            blocked:
                                0,

                            inactive:
                                0,

                            owners:
                                0,
                        }
                    );

                },
                [
                    users,
                ]
            );


        /*
        |--------------------------------------------------------------------------
        | PAGINATION
        |--------------------------------------------------------------------------
        */

        const currentPage =
            Math.max(
                Number(
                    pagination?.page ||
                    filters?.page ||
                    1
                ),
                1
            );


        const totalPages =
            Math.max(
                Number(
                    pagination?.totalPages ||
                    1
                ),
                1
            );


        /*
        |--------------------------------------------------------------------------
        | REFRESH
        |--------------------------------------------------------------------------
        */

        const refreshList =
            () => {

                dispatch(
                    fetchPlatformTenantUsers({
                        page:
                            filters?.page ||
                            1,

                        limit:
                            filters?.limit ||
                            20,

                        search:
                            filters?.search ||
                            "",

                        status:
                            filters?.status ||
                            "",

                        userType:
                            filters?.userType ||
                            "",

                        tenantId:
                            filters?.tenantId ||
                            "",

                        roleId:
                            filters?.roleId ||
                            "",
                    })
                );

            };


        /*
        |--------------------------------------------------------------------------
        | SEARCH
        |--------------------------------------------------------------------------
        */

        const handleSearch =
            (
                event
            ) => {

                event.preventDefault();


                dispatch(
                    setPlatformTenantUserFilter({
                        key:
                            "page",

                        value:
                            1,
                    })
                );


                dispatch(
                    setPlatformTenantUserFilter({
                        key:
                            "search",

                        value:
                            searchInput.trim(),
                    })
                );

            };


        /*
        |--------------------------------------------------------------------------
        | CLEAR SEARCH
        |--------------------------------------------------------------------------
        */

        const handleClearSearch =
            () => {

                setSearchInput(
                    ""
                );


                dispatch(
                    setPlatformTenantUserFilter({
                        key:
                            "page",

                        value:
                            1,
                    })
                );


                dispatch(
                    setPlatformTenantUserFilter({
                        key:
                            "search",

                        value:
                            "",
                    })
                );

            };


        /*
        |--------------------------------------------------------------------------
        | FILTER
        |--------------------------------------------------------------------------
        */

        const handleFilterChange =
            (
                event
            ) => {

                const {
                    name,
                    value,
                } =
                    event.target;


                dispatch(
                    setPlatformTenantUserFilter({
                        key:
                            "page",

                        value:
                            1,
                    })
                );


                dispatch(
                    setPlatformTenantUserFilter({
                        key:
                            name,

                        value,
                    })
                );


                /*
                |--------------------------------------------------------------------------
                | CLEAR ROLE WHEN TENANT CHANGES
                |--------------------------------------------------------------------------
                */

                if (
                    name ===
                    "tenantId"
                ) {

                    dispatch(
                        setPlatformTenantUserFilter({
                            key:
                                "roleId",

                            value:
                                "",
                        })
                    );

                }

            };


        /*
        |--------------------------------------------------------------------------
        | PAGE CHANGE
        |--------------------------------------------------------------------------
        */

        const handlePageChange =
            (
                page
            ) => {

                const nextPage =
                    Math.min(
                        Math.max(
                            Number(
                                page
                            ) ||
                            1,
                            1
                        ),
                        totalPages
                    );


                dispatch(
                    setPlatformTenantUserFilter({
                        key:
                            "page",

                        value:
                            nextPage,
                    })
                );

            };


        /*
        |--------------------------------------------------------------------------
        | VIEW USER
        |--------------------------------------------------------------------------
        */

        const handleViewUser =
            (
                user
            ) => {

                const id =
                    getUserId(
                        user
                    );


                if (
                    !id
                ) {

                    return;

                }


                history.push(
                    `/platform/users/${id}`
                );

            };


        /*
        |--------------------------------------------------------------------------
        | UPDATE STATUS
        |--------------------------------------------------------------------------
        */

        const handleUpdateStatus =
            async (
                user,
                nextStatus
            ) => {

                const id =
                    getUserId(
                        user
                    );


                if (
                    !id ||
                    statusLoading
                ) {

                    return;

                }


                const normalizedStatus =
                    String(
                        nextStatus ||
                        ""
                    )
                        .trim()
                        .toUpperCase();


                let title =
                    "Update User Status?";


                let text =
                    `Update ${user?.name || "this user"} to ${normalizedStatus}?`;


                let confirmButtonText =
                    "Update";


                if (
                    normalizedStatus ===
                    "BLOCKED"
                ) {

                    title =
                        "Block Tenant User?";

                    text =
                        `${user?.name || "This user"} will no longer be able to access the tenant account.`;

                    confirmButtonText =
                        "Block";

                }


                if (
                    normalizedStatus ===
                    "ACTIVE"
                ) {

                    title =
                        "Activate Tenant User?";

                    text =
                        `${user?.name || "This user"} will regain access to the tenant account.`;

                    confirmButtonText =
                        "Activate";

                }


                if (
                    normalizedStatus ===
                    "INACTIVE"
                ) {

                    title =
                        "Deactivate Tenant User?";

                    text =
                        `${user?.name || "This user"} will temporarily lose tenant access.`;

                    confirmButtonText =
                        "Deactivate";

                }


                const confirmation =
                    await swal.confirm({
                        title,
                        text,
                        confirmButtonText,
                    });


                if (
                    confirmation
                        ?.isConfirmed ===
                    false
                ) {

                    return;

                }


                const result =
                    await dispatch(
                        updatePlatformTenantUserStatus({
                            id,
                            status:
                                normalizedStatus,
                        })
                    );


                if (
                    updatePlatformTenantUserStatus
                        .fulfilled
                        .match(
                            result
                        )
                ) {

                    await swal.success({
                        title:
                            "Status Updated",

                        text:
                            result?.payload
                                ?.response
                                ?.message ||
                            "Tenant user status updated successfully.",
                    });


                    refreshList();


                    return;

                }


                swal.error({
                    title:
                        "Update Failed",

                    text:
                        result?.payload
                            ?.message ||
                        "Unable to update tenant user status.",
                });

            };


        /*
        |--------------------------------------------------------------------------
        | UI
        |--------------------------------------------------------------------------
        */

        return (


                <div className="platform-tenant-users-page">

                    {/*
                    |--------------------------------------------------------------------------
                    | HEADER
                    |--------------------------------------------------------------------------
                    */}

                    <header className="platform-tenant-users-header">

                        <div>

                            <button
                                type="button"
                                className="platform-tenant-users-back"
                                onClick={() =>
                                    history.push(
                                        "/platform/dashboard"
                                    )
                                }
                            >

                                <FiArrowLeft />

                                Dashboard

                            </button>


                            <h1>

                                Tenant Users

                            </h1>


                            <p>

                                View tenant owners and employees across all businesses and control account access.

                            </p>

                        </div>


                        <button
                            type="button"
                            className="platform-tenant-users-refresh"
                            onClick={
                                refreshList
                            }
                            disabled={
                                listLoading
                            }
                        >

                            <FiRefreshCw
                                className={
                                    listLoading
                                        ? "platform-tenant-users-spin"
                                        : ""
                                }
                            />

                            Refresh

                        </button>

                    </header>


                    {/*
                    |--------------------------------------------------------------------------
                    | SUMMARY
                    |--------------------------------------------------------------------------
                    */}

                    <section className="platform-tenant-users-summary">

                        <article>

                            <div>
                                <FiUsers />
                            </div>

                            <span>
                                Total Users
                            </span>

                            <strong>
                                {
                                    formatNumber(
                                        pagination?.total
                                    )
                                }
                            </strong>

                        </article>


                        <article>

                            <div>
                                <FiCheckCircle />
                            </div>

                            <span>
                                Active On Page
                            </span>

                            <strong>
                                {
                                    summary.active
                                }
                            </strong>

                        </article>


                        <article>

                            <div>
                                <FiUser />
                            </div>

                            <span>
                                Owners On Page
                            </span>

                            <strong>
                                {
                                    summary.owners
                                }
                            </strong>

                        </article>


                        <article>

                            <div>
                                <FiXCircle />
                            </div>

                            <span>
                                Inactive On Page
                            </span>

                            <strong>
                                {
                                    summary.inactive
                                }
                            </strong>

                        </article>


                        <article>

                            <div>
                                <FiSlash />
                            </div>

                            <span>
                                Blocked On Page
                            </span>

                            <strong>
                                {
                                    summary.blocked
                                }
                            </strong>

                        </article>

                    </section>


                    {/*
                    |--------------------------------------------------------------------------
                    | SEARCH
                    |--------------------------------------------------------------------------
                    */}

                    <section className="platform-tenant-users-toolbar">

                        <form
                            className="platform-tenant-users-search"
                            onSubmit={
                                handleSearch
                            }
                        >

                            <FiSearch />


                            <input
                                type="text"
                                value={
                                    searchInput
                                }
                                onChange={(
                                    event
                                ) =>
                                    setSearchInput(
                                        event
                                            .target
                                            .value
                                    )
                                }
                                placeholder="Search name, mobile or email..."
                            />


                            {searchInput ? (

                                <button
                                    type="button"
                                    className="platform-tenant-users-clear"
                                    onClick={
                                        handleClearSearch
                                    }
                                >

                                    <FiX />

                                </button>

                            ) : null}


                            <button
                                type="submit"
                                disabled={
                                    listLoading
                                }
                            >

                                Search

                            </button>

                        </form>

                    </section>


                    {/*
                    |--------------------------------------------------------------------------
                    | FILTERS
                    |--------------------------------------------------------------------------
                    */}

                    <section className="platform-tenant-users-filters">

                        <div>

                            <FiFilter />

                            <span>
                                Filters
                            </span>

                        </div>


                        <select
                            name="tenantId"
                            value={
                                filters?.tenantId ||
                                ""
                            }
                            onChange={
                                handleFilterChange
                            }
                            disabled={
                                filtersLoading
                            }
                        >

                            <option value="">

                                All Businesses

                            </option>


                            {(
                                filterOptions
                                    ?.tenants ||
                                []
                            ).map(
                                (
                                    tenant
                                ) => (

                                    <option
                                        key={
                                            tenant?._id ||
                                            tenant?.id
                                        }
                                        value={
                                            tenant?._id ||
                                            tenant?.id
                                        }
                                    >

                                        {
                                            tenant?.name ||
                                            tenant?.code ||
                                            "Business"
                                        }

                                    </option>

                                )
                            )}

                        </select>


                        <select
                            name="userType"
                            value={
                                filters?.userType ||
                                ""
                            }
                            onChange={
                                handleFilterChange
                            }
                        >

                            <option value="">

                                All User Types

                            </option>

                            <option value="OWNER">

                                Owner

                            </option>

                            <option value="EMPLOYEE">

                                Employee

                            </option>

                        </select>


                        <select
                            name="status"
                            value={
                                filters?.status ||
                                ""
                            }
                            onChange={
                                handleFilterChange
                            }
                        >

                            <option value="">

                                All Status

                            </option>

                            <option value="ACTIVE">

                                Active

                            </option>

                            <option value="INACTIVE">

                                Inactive

                            </option>

                            <option value="BLOCKED">

                                Blocked

                            </option>

                        </select>


                        <select
                            name="roleId"
                            value={
                                filters?.roleId ||
                                ""
                            }
                            onChange={
                                handleFilterChange
                            }
                            disabled={
                                filtersLoading
                            }
                        >

                            <option value="">

                                All Roles

                            </option>


                            {visibleRoles.map(
                                (
                                    role
                                ) => (

                                    <option
                                        key={
                                            role?._id ||
                                            role?.id
                                        }
                                        value={
                                            role?._id ||
                                            role?.id
                                        }
                                    >

                                        {
                                            role?.name ||
                                            role?.code ||
                                            "Role"
                                        }

                                    </option>

                                )
                            )}

                        </select>

                    </section>


                    {/*
                    |--------------------------------------------------------------------------
                    | TABLE
                    |--------------------------------------------------------------------------
                    */}

                    <section className="platform-tenant-users-table-card">

                        <div className="platform-tenant-users-table-wrapper">

                            <table className="platform-tenant-users-table">

                                <thead>

                                    <tr>

                                        <th>
                                            User
                                        </th>

                                        <th>
                                            Business
                                        </th>

                                        <th>
                                            Role
                                        </th>

                                        <th>
                                            Type
                                        </th>

                                        <th>
                                            Status
                                        </th>

                                        <th>
                                            Verification
                                        </th>

                                        <th>
                                            Last Login
                                        </th>

                                        <th>
                                            Actions
                                        </th>

                                    </tr>

                                </thead>


                                <tbody>

                                    {listLoading &&
                                    users.length ===
                                        0 ? (

                                        <tr>

                                            <td
                                                colSpan={
                                                    8
                                                }
                                            >

                                                <div className="platform-tenant-users-state">

                                                    <div className="platform-business-loader" />

                                                    <strong>

                                                        Loading tenant users...

                                                    </strong>

                                                </div>

                                            </td>

                                        </tr>

                                    ) : users.length >
                                      0 ? (

                                        users.map(
                                            (
                                                user
                                            ) => {

                                                const userId =
                                                    getUserId(
                                                        user
                                                    );


                                                const tenant =
                                                    getTenant(
                                                        user
                                                    );


                                                const role =
                                                    getRole(
                                                        user
                                                    );


                                                const rowLoading =
                                                    statusLoading &&
                                                    String(
                                                        statusUserId
                                                    ) ===
                                                        String(
                                                            userId
                                                        );


                                                const userStatus =
                                                    String(
                                                        user?.status ||
                                                        ""
                                                    )
                                                        .trim()
                                                        .toUpperCase();


                                                return (

                                                    <tr
                                                        key={
                                                            userId
                                                        }
                                                    >

                                                        <td>

                                                            <div className="platform-tenant-user-cell">

                                                                <div className="platform-tenant-user-avatar">

                                                                    {
                                                                        String(
                                                                            user?.name ||
                                                                            "U"
                                                                        )
                                                                            .charAt(
                                                                                0
                                                                            )
                                                                            .toUpperCase()
                                                                    }

                                                                </div>


                                                                <div>

                                                                    <strong>

                                                                        {
                                                                            user?.name ||
                                                                            "-"
                                                                        }

                                                                    </strong>


                                                                    <span>

                                                                        {
                                                                            user?.mobile ||
                                                                            "-"
                                                                        }

                                                                    </span>


                                                                    <small>

                                                                        {
                                                                            user?.email ||
                                                                            ""
                                                                        }

                                                                    </small>

                                                                </div>

                                                            </div>

                                                        </td>


                                                        <td>

                                                            <div className="platform-tenant-business-cell">

                                                                <FiBriefcase />


                                                                <div>

                                                                    <strong>

                                                                        {
                                                                            tenant?.name ||
                                                                            "-"
                                                                        }

                                                                    </strong>


                                                                    <span>

                                                                        {
                                                                            tenant?.code ||
                                                                            "-"
                                                                        }

                                                                    </span>

                                                                </div>

                                                            </div>

                                                        </td>


                                                        <td>

                                                            <div className="platform-tenant-role-cell">

                                                                <FiShield />


                                                                <div>

                                                                    <strong>

                                                                        {
                                                                            role?.name ||
                                                                            user?.role ||
                                                                            "-"
                                                                        }

                                                                    </strong>


                                                                    <span>

                                                                        {
                                                                            role?.code ||
                                                                            ""
                                                                        }

                                                                    </span>

                                                                </div>

                                                            </div>

                                                        </td>


                                                        <td>

                                                            <span className={`platform-tenant-user-type ${
                                                                user?.userType ===
                                                                "OWNER"
                                                                    ? "owner"
                                                                    : "employee"
                                                            }`}>

                                                                {
                                                                    user?.userType ||
                                                                    "-"
                                                                }

                                                            </span>

                                                        </td>


                                                        <td>

                                                            <BusinessStatusBadge
                                                                status={
                                                                    user?.status
                                                                }
                                                            />

                                                        </td>


                                                        <td>

                                                            <div className="platform-tenant-verification">

                                                                <span
                                                                    className={
                                                                        user?.isVerified
                                                                            ? "verified"
                                                                            : "not-verified"
                                                                    }
                                                                >

                                                                    {
                                                                        user?.isVerified
                                                                            ? "Account ✓"
                                                                            : "Account ✕"
                                                                    }

                                                                </span>


                                                                <span
                                                                    className={
                                                                        user?.mobileVerified
                                                                            ? "verified"
                                                                            : "not-verified"
                                                                    }
                                                                >

                                                                    {
                                                                        user?.mobileVerified
                                                                            ? "Mobile ✓"
                                                                            : "Mobile ✕"
                                                                    }

                                                                </span>

                                                            </div>

                                                        </td>


                                                        <td>

                                                            <span className="platform-tenant-last-login">

                                                                {
                                                                    formatDate(
                                                                        user?.lastLogin
                                                                    )
                                                                }

                                                            </span>

                                                        </td>


                                                        <td>

                                                            <div className="platform-tenant-user-actions">

                                                                <button
                                                                    type="button"
                                                                    title="View User"
                                                                    onClick={() =>
                                                                        handleViewUser(
                                                                            user
                                                                        )
                                                                    }
                                                                >

                                                                    <FiEye />

                                                                </button>


                                                                {userStatus !==
                                                                "ACTIVE" ? (

                                                                    <button
                                                                        type="button"
                                                                        className="activate"
                                                                        title="Activate User"
                                                                        disabled={
                                                                            rowLoading
                                                                        }
                                                                        onClick={() =>
                                                                            handleUpdateStatus(
                                                                                user,
                                                                                "ACTIVE"
                                                                            )
                                                                        }
                                                                    >

                                                                        <FiCheckCircle />

                                                                    </button>

                                                                ) : null}


                                                                {userStatus ===
                                                                "ACTIVE" ? (

                                                                    <button
                                                                        type="button"
                                                                        className="inactive"
                                                                        title="Deactivate User"
                                                                        disabled={
                                                                            rowLoading
                                                                        }
                                                                        onClick={() =>
                                                                            handleUpdateStatus(
                                                                                user,
                                                                                "INACTIVE"
                                                                            )
                                                                        }
                                                                    >

                                                                        <FiXCircle />

                                                                    </button>

                                                                ) : null}


                                                                {userStatus !==
                                                                "BLOCKED" ? (

                                                                    <button
                                                                        type="button"
                                                                        className="block"
                                                                        title="Block User"
                                                                        disabled={
                                                                            rowLoading
                                                                        }
                                                                        onClick={() =>
                                                                            handleUpdateStatus(
                                                                                user,
                                                                                "BLOCKED"
                                                                            )
                                                                        }
                                                                    >

                                                                        <FiSlash />

                                                                    </button>

                                                                ) : null}

                                                            </div>

                                                        </td>

                                                    </tr>

                                                );

                                            }
                                        )

                                    ) : (

                                        <tr>

                                            <td
                                                colSpan={
                                                    8
                                                }
                                            >

                                                <div className="platform-tenant-users-empty">

                                                    <FiUsers />


                                                    <strong>

                                                        No tenant users found

                                                    </strong>


                                                    <span>

                                                        Try changing the search or filters.

                                                    </span>

                                                </div>

                                            </td>

                                        </tr>

                                    )}

                                </tbody>

                            </table>

                        </div>


                        {/*
                        |--------------------------------------------------------------------------
                        | PAGINATION
                        |--------------------------------------------------------------------------
                        */}

                        <div className="platform-tenant-users-pagination">

                            <span>

                                Page{" "}

                                <strong>
                                    {
                                        currentPage
                                    }
                                </strong>

                                {" "}of{" "}

                                <strong>
                                    {
                                        totalPages
                                    }
                                </strong>

                                {" "}·{" "}

                                {
                                    formatNumber(
                                        pagination?.total
                                    )
                                }

                                {" "}users

                            </span>


                            <div>

                                <button
                                    type="button"
                                    disabled={
                                        listLoading ||
                                        currentPage <=
                                            1
                                    }
                                    onClick={() =>
                                        handlePageChange(
                                            currentPage -
                                            1
                                        )
                                    }
                                >

                                    Previous

                                </button>


                                <span>

                                    {
                                        currentPage
                                    }

                                </span>


                                <button
                                    type="button"
                                    disabled={
                                        listLoading ||
                                        currentPage >=
                                            totalPages
                                    }
                                    onClick={() =>
                                        handlePageChange(
                                            currentPage +
                                            1
                                        )
                                    }
                                >

                                    Next

                                </button>

                            </div>

                        </div>

                    </section>

                </div>


        );

    };


export default PlatformTenantUserList;